import { useEffect, useRef } from "react";
import { Instagram, Linkedin } from "lucide-react";

const HERO_IMAGE =
    "https://customer-assets.emergentagent.com/job_a4c9c179-d042-4742-b732-bd5e37197666/artifacts/sw53cej4_Image%206.jpeg";

/**
 * Slide-export view at /slide — locked at 16:9 (1920×1080).
 * Same visual system as the hero, but with:
 *   - No BUSHEL wordmark / leaf logo
 *   - No "Coming Soon 2026" eyebrow or status pill
 *   - No Contact Us / Investor Access buttons
 * Everything else (headline, subheadline, YesHealth partnership,
 * footer social + copyright) is retained.
 */
export const SlideExport = () => {
    const ref = useRef(null);
    useEffect(() => {
        // Neutralize App-level dark backgrounds so the frame is captured cleanly.
        const targets = [
            document.documentElement,
            document.body,
            document.getElementById("root"),
            document.querySelector(".App"),
        ].filter(Boolean);
        const prev = targets.map((t) => t.style.background);
        targets.forEach((t) => (t.style.background = "#050C08"));
        return () => targets.forEach((t, i) => (t.style.background = prev[i] || ""));
    }, []);

    return (
        <div
            ref={ref}
            data-export-root="true"
            style={{
                width: 1920,
                height: 1080,
                position: "relative",
                overflow: "hidden",
                background: "#050C08",
                color: "#F7F9F5",
                fontFamily: "'Cabinet Grotesk', 'Manrope', system-ui, sans-serif",
            }}
        >
            {/* Hero image */}
            <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
                <img
                    src={HERO_IMAGE}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
            </div>

            {/* Left-to-right cinematic overlay */}
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 10,
                    background:
                        "linear-gradient(to right, rgba(5,12,8,0.95), rgba(5,12,8,0.65) 45%, transparent 90%)",
                }}
            />
            {/* Bottom vignette */}
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "66%",
                    zIndex: 10,
                    background:
                        "linear-gradient(to top, rgba(5,12,8,0.85), rgba(5,12,8,0.30) 60%, transparent)",
                }}
            />
            {/* Warm dusk glow (static) */}
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    top: "18%",
                    right: "-8%",
                    width: "55%",
                    height: "55%",
                    zIndex: 10,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle at center, rgba(229,169,83,0.20), rgba(229,169,83,0.06) 40%, transparent 70%)",
                    filter: "blur(80px)",
                    mixBlendMode: "screen",
                }}
            />

            {/* Content */}
            <section
                style={{
                    position: "relative",
                    zIndex: 20,
                    display: "flex",
                    height: "100%",
                    alignItems: "flex-end",
                    padding: "0 80px 150px",
                }}
            >
                <div style={{ maxWidth: 900 }}>
                    <h1
                        style={{
                            fontFamily:
                                "'Cabinet Grotesk', 'Manrope', system-ui, sans-serif",
                            fontSize: 128,
                            fontWeight: 800,
                            letterSpacing: "-0.015em",
                            lineHeight: 1.02,
                            color: "#F7F9F5",
                            margin: 0,
                        }}
                    >
                        <span style={{ display: "block" }}>
                            Grown{" "}
                            <em
                                style={{
                                    fontStyle: "normal",
                                    fontWeight: 500,
                                    color: "#89F337",
                                }}
                            >
                                clean.
                            </em>
                        </span>
                        <span style={{ display: "block" }}>Grown local.</span>
                        <span style={{ display: "block" }}>
                            Year{" "}
                            <span
                                style={{ position: "relative", display: "inline-block" }}
                            >
                                round.
                                <span
                                    aria-hidden="true"
                                    style={{
                                        position: "absolute",
                                        left: 0,
                                        right: 0,
                                        bottom: -4,
                                        height: 4,
                                        background: "rgba(137,243,55,0.85)",
                                    }}
                                />
                            </span>
                        </span>
                    </h1>

                    <p
                        style={{
                            marginTop: 40,
                            maxWidth: 780,
                            fontSize: 24,
                            lineHeight: 1.55,
                            letterSpacing: "0.01em",
                            color: "#C9D3CC",
                        }}
                    >
                        Pesticide-free, herbicide-free, and fungicide-free,
                        nutrient-rich leafy greens grown year round in an
                        advanced indoor vertical farm, located in Strathroy,
                        Ontario.
                    </p>

                    <div
                        style={{
                            marginTop: 44,
                            maxWidth: 520,
                            paddingLeft: 22,
                            borderLeft: "2px solid rgba(137,243,55,0.6)",
                        }}
                    >
                        <p
                            style={{
                                fontSize: 13,
                                fontWeight: 500,
                                letterSpacing: "0.32em",
                                textTransform: "uppercase",
                                color: "#A3B3A8",
                                margin: 0,
                            }}
                        >
                            In Partnership With
                        </p>
                        <p
                            style={{
                                marginTop: 10,
                                fontFamily: "'Playfair Display', serif",
                                fontSize: 48,
                                fontWeight: 500,
                                lineHeight: 1.15,
                                color: "#F7F9F5",
                            }}
                        >
                            YesHealth Group
                        </p>
                        <p
                            style={{
                                marginTop: 6,
                                fontSize: 18,
                                letterSpacing: "0.01em",
                                color: "#A3B3A8",
                            }}
                        >
                            Global vertical farming technology leader
                        </p>
                    </div>
                </div>
            </section>

            {/* Footer — social icons + copyright */}
            <footer
                style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 80px 48px",
                }}
            >
                <div style={{ display: "flex", gap: 12 }}>
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 44,
                            height: 44,
                            borderRadius: 999,
                            border: "1px solid rgba(255,255,255,0.15)",
                            background: "rgba(255,255,255,0.05)",
                            color: "#A3B3A8",
                        }}
                    >
                        <Instagram style={{ width: 18, height: 18 }} strokeWidth={1.75} />
                    </span>
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 44,
                            height: 44,
                            borderRadius: 999,
                            border: "1px solid rgba(255,255,255,0.15)",
                            background: "rgba(255,255,255,0.05)",
                            color: "#A3B3A8",
                        }}
                    >
                        <Linkedin style={{ width: 18, height: 18 }} strokeWidth={1.75} />
                    </span>
                </div>
                <p
                    style={{
                        fontSize: 14,
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        color: "#708075",
                    }}
                >
                    © 2026 Bushel Agritech Inc. All rights reserved. Bushel
                    <sup style={{ fontSize: "0.7em", verticalAlign: "super" }}>TM</sup>
                </p>
            </footer>
        </div>
    );
};

export default SlideExport;
